import { color, Common } from '@stockbit/ui';
import Link from 'next/link';
import { LabelVerifiedAccount } from 'global/Labels';
import Image from 'global/Image';
import { AVATAR_URL } from 'constants/api';
import ImageSlider from 'global/ImageSlider';
import { handleErrorAvatar } from 'utils/general';
import { PROJECT_FOLDER } from 'constants/app';
import {
  DATA_CY_NAME_CHAT_PAGE_MAIN_CONTENT_CHAT_PREVIEW_REPOST_BOX,
  DATA_CY_NAME_CHAT_PAGE_MAIN_CONTENT_CHAT_PREVIEW_REPOST_BOX_AVATAR,
  DATA_CY_NAME_CHAT_PAGE_MAIN_CONTENT_CHAT_PREVIEW_REPOST_BOX_CONTENT,
  DATA_CY_NAME_CHAT_PAGE_MAIN_CONTENT_CHAT_PREVIEW_REPOST_BOX_DOCUMENT_ATTACHMENT,
  DATA_CY_NAME_CHAT_PAGE_MAIN_CONTENT_CHAT_PREVIEW_REPOST_BOX_IMAGE,
  DATA_CY_NAME_CHAT_PAGE_MAIN_CONTENT_CHAT_PREVIEW_REPOST_BOX_NEWS_SOURCE,
  DATA_CY_NAME_CHAT_PAGE_MAIN_CONTENT_CHAT_PREVIEW_REPOST_BOX_TITLE,
  DATA_CY_NAME_CHAT_PAGE_MAIN_CONTENT_CHAT_PREVIEW_REPOST_BOX_UNAVAILABLE,
  DATA_CY_NAME_CHAT_PAGE_MAIN_CONTENT_CHAT_PREVIEW_REPOST_BOX_USERNAME,
  DATA_CY_NAME_CHAT_PAGE_MAIN_CONTENT_CHAT_PREVIEW_REPOST_BOX_YOUTUBE_PLAYER,
} from 'features/chat/MainContent/ChatPreview/__tests__/constant';
import TextContent from '../TextContent';
import { RepostBoxContainer, RepostImageContainer } from './styled';
import { StreamPostData } from '../../../../@types/stream';
import YoutubePlayer from './YoutubePlayer';
import DocumentContainer from './DocumentContainer';

const { Flex, Avatar, Text } = Common;

export interface RepostBoxProps {
  post: StreamPostData;
}

function RepostBox({ post }: RepostBoxProps) {
  const {
    content_original: content,
    title,
    newsfeed_source: newsSource,
    newsfeed_label: newsLabel,
    newsfeed_img: newsImg,
    avatar,
    username,
    // images,
    postid,
    official,
    is_unavailable: isUnavailable,
    mask_html: maskHtml,
    images_data: imagesData,
    image_frame_type: imageFrameType,
    attachment,
  } = post;

  if (isUnavailable) {
    return (
      <Link href={`/post/${postid}`}>
        <a
          target="_blank"
          rel="noopener noreferrer"
          data-cy={
            DATA_CY_NAME_CHAT_PAGE_MAIN_CONTENT_CHAT_PREVIEW_REPOST_BOX_UNAVAILABLE
          }
        >
          <RepostBoxContainer>
            <Text align="center" color={color.highgray}>
              This post is unavailable.
            </Text>
          </RepostBoxContainer>
        </a>
      </Link>
    );
  }

  const avatarUrl = avatar.startsWith('http')
    ? avatar
    : `${AVATAR_URL}/${avatar || ''}`;
  const isHaveImage = Boolean(imagesData?.length > 0 && imagesData[0]?.image);

  const handleClickRepostBox = () => {
    const path =
      !PROJECT_FOLDER || PROJECT_FOLDER === '/' ? '/' : `/${PROJECT_FOLDER}/`;
    window.open(
      `${window.location.origin}${path}post/${post.postid}`,
      '_blank',
    );
  };

  return (
    <RepostBoxContainer
      onClick={handleClickRepostBox}
      data-cy={DATA_CY_NAME_CHAT_PAGE_MAIN_CONTENT_CHAT_PREVIEW_REPOST_BOX}
    >
      <Flex align="center" mb="16px">
        <Avatar
          size="x-small"
          mr="12px"
          data-cy={
            DATA_CY_NAME_CHAT_PAGE_MAIN_CONTENT_CHAT_PREVIEW_REPOST_BOX_AVATAR
          }
        >
          <Image
            src={avatarUrl}
            alt={username}
            onError={(e) => handleErrorAvatar(e, username)}
          />
        </Avatar>
        <Text
          family="bold"
          size="16px"
          data-cy={
            DATA_CY_NAME_CHAT_PAGE_MAIN_CONTENT_CHAT_PREVIEW_REPOST_BOX_USERNAME
          }
        >
          {username}
        </Text>
        <LabelVerifiedAccount
          deprecatedVerifiedStatus={official > 0}
          verifiedStatus={post?.verified_status}
        />
      </Flex>
      {isHaveImage && (
        <RepostImageContainer
          data-cy={
            DATA_CY_NAME_CHAT_PAGE_MAIN_CONTENT_CHAT_PREVIEW_REPOST_BOX_IMAGE
          }
        >
          <div style={{ clear: 'both' }} />
          <ImageSlider
            images={imagesData}
            frameType={imageFrameType}
            isRepost
          />
        </RepostImageContainer>
      )}
      {title && (
        <Text
          color={color.primary}
          mb="8px"
          data-cy={
            DATA_CY_NAME_CHAT_PAGE_MAIN_CONTENT_CHAT_PREVIEW_REPOST_BOX_TITLE
          }
        >
          {title}
        </Text>
      )}
      <Text
        height="22.475px"
        data-cy={
          DATA_CY_NAME_CHAT_PAGE_MAIN_CONTENT_CHAT_PREVIEW_REPOST_BOX_CONTENT
        }
      >
        <TextContent
          content={content}
          maskedHtml={maskHtml}
          isDefaultExpand={false}
          isRepost
          style={{ marginBottom: 0 }}
        />
      </Text>
      {!isHaveImage && (
        <YoutubePlayer
          content={content}
          dataCyName={
            DATA_CY_NAME_CHAT_PAGE_MAIN_CONTENT_CHAT_PREVIEW_REPOST_BOX_YOUTUBE_PLAYER
          }
        />
      )}
      {newsSource && (
        <Flex
          mt="10px"
          data-cy={
            DATA_CY_NAME_CHAT_PAGE_MAIN_CONTENT_CHAT_PREVIEW_REPOST_BOX_NEWS_SOURCE
          }
        >
          <Image
            alt={newsLabel}
            src={newsImg}
            style={{ width: '16px', marginRight: '8px' }}
          />
          <Text size="12px">{newsLabel}</Text>
        </Flex>
      )}
      {attachment?.length > 0 && (
        <DocumentContainer
          documents={attachment}
          dataCyName={
            DATA_CY_NAME_CHAT_PAGE_MAIN_CONTENT_CHAT_PREVIEW_REPOST_BOX_DOCUMENT_ATTACHMENT
          }
        />
      )}
    </RepostBoxContainer>
  );
}

export default RepostBox;
